import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import * as Sentry from '@sentry/react';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import BasicTable from './basicTable';
import useGetUsers from '../../apiHooks/queries/useGetUsers';
import { USERS_TABLE } from '../../constants/pageIdentifiers';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';
import { UserInterface } from '../../interfaces/user.interface';
import BasicDialog from '../../dialogs/basicDialog';
import UsersForm from '../../forms/usersForm';
import { useDeleteUser, useGenerateNewPassword } from '../../apiHooks/mutations/userMutations/userMutations';
import { userFormInputs } from '../../constants/modalInputs/userFormInputs';
import DateCell from '../../partials/dateCell';
import NewPasswordDialogContent from '../newPasswordDialogContent/newPasswordDialogContent';
import ConfirmDialog from '../../dialogs/confirmDialog';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import { useGetExportUsersTable } from '../../apiHooks/mutations/reportsExportMutations/useGetExportUsersTable';

const columns: TableColumnInterface[] = [
  { field: 'firstName', headerName: 'firstName' },
  { field: 'lastName', headerName: 'lastName' },
  { field: 'email', headerName: 'email' },
  {
    field: 'createdAt',
    headerName: 'createdAt',
    cellRenderer: (params: string) => <DateCell params={params} />,
  },
];

const sortablesList = ['firstName', 'lastName', 'email', 'createdAt'];

const UsersTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [editData, setEditData] = useState<UserInterface>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const { mutateAsync: deleteUser, isLoading: isDeleting } = useDeleteUser();
  const { mutateAsync: generateNewPassword, isLoading: isGeneratingNewPassword } = useGenerateNewPassword();
  const [searchParams] = useSearchParams();
  const [isAreYouSureDialogOpen, setIsAreYouSureDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>(''); // State for user name
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || '');
  const [orderBy, setOrderBy] = useState<string>(searchParams.get('orderBy') || '');
  const [searchInput, setSearchInput] = useState({
    firstName: searchParams.get('firstName') || '',
    lastName: searchParams.get('lastName') || '',
    email: searchParams.get('email') || '',
  });

  const { t } = useTranslation();

  const usersFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);
  const setUsersFilterDateRange = useDashboardFilterDateStore((state) => state.setDashboardFilterDateRange);

  const {
    data: usersData,
    isError,
    refetch: refetchUsersData,
    isFetching,
  } = useGetUsers(
    USERS_TABLE,
    searchInput.firstName,
    searchInput.lastName,
    searchInput.email,
    orderBy,
    sortBy,
    usersFilterDateRange[0],
    usersFilterDateRange[1],
    rowsPerPage,
    page + 1,
  );

  useEffect(() => {
    setPage(0);
  }, [usersFilterDateRange, sortBy, orderBy]);

  const handleOnDelete = async (id: number) => {
    await deleteUser(id);
    await refetchUsersData();
    setIsAreYouSureDialogOpen(false);
  };

  const { mutate: fetchDownload } = useGetExportUsersTable(
    {
      identifier: 'EXPORT_USERS',
      firstName: searchInput.firstName,
      lastName: searchInput.lastName,
      email: searchInput.email,
      from: usersFilterDateRange[0],
      to: usersFilterDateRange[1],
      orderBy,
      sortBy,
      rows: rowsPerPage,
      page: page + 1,
    },
  );

  const handleGenerateNewPassword = async (id: number | undefined) => {
    const response = await generateNewPassword(id);
    setNewPassword(response?.data?.data?.password);
    setIsPasswordDialogOpen(true);
  };

  const closeEditDialogAndRefetch = async () => {
    setIsEditDialogOpen(false);
    await refetchUsersData();
  };

  const handleDeleteClick = (id: number, firstName: string, lastName: string, email: string) => {
    setSelectedUserId(id);
    const userName = `${firstName || email} ${lastName || ''}`.trim();
    setSelectedUserName(firstName);
    console.log('Selected User Name:', firstName);
    setIsAreYouSureDialogOpen(true);
  };

  return (
    <>
      <BasicDialog
        closeDialog={() => setIsEditDialogOpen(false)}
        open={isEditDialogOpen}
        title={editData?.id ? t('updateUser') : t('createUser')}
      >
        <UsersForm
          data={editData && editData}
          closeDialog={closeEditDialogAndRefetch}
          handleAfterCreate={(newUserPassword) => { setNewPassword(newUserPassword); setIsPasswordDialogOpen(true); }}
        />
      </BasicDialog>
      <BasicDialog
        closeDialog={() => setIsPasswordDialogOpen(false)}
        open={isPasswordDialogOpen && !!newPassword}
        title={t('newPassword')}
      >
        <NewPasswordDialogContent
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          setIsPasswordDialogOpen={setIsPasswordDialogOpen}
        />
      </BasicDialog>

      <ConfirmDialog
        text={`Are you sure you want to delete the user "${selectedUserName}"?`} // Display selected user's name
        title="Delete User"
        variant="warning"
        onCloseDialog={() => setIsAreYouSureDialogOpen(false)}
        open={isAreYouSureDialogOpen}
        onSubmit={() => {
          if (selectedUserId !== null) {
            handleOnDelete(selectedUserId);
          }
        }}
        isDisabled={isDeleting}
      />

      <BasicTable
        data={usersData?.data}
        fetchDownload={fetchDownload}
        refetch={refetchUsersData}
        onDelete={(id, item: any) => handleDeleteClick(id, item?.firstName, item?.lastName, item?.email)}
        onGeneratePassword={(item) => handleGenerateNewPassword(item.id)}
        formInputs={userFormInputs}
        setPage={(newPage) => setPage(newPage)}
        onEdit={(item: UserInterface) => setEditData(item)}
        setEditDialogIsOpen={(isOpen) => setIsEditDialogOpen(isOpen)}
        setRowsPerPage={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
        setSearchInput={setSearchInput}
        setSortBy={setSortBy}
        setOrderBy={setOrderBy}
        filterDateRange={usersFilterDateRange}
        setFilterDateRange={setUsersFilterDateRange}
        canGenerateNewPassword
        {...{
          isError,
          isFetching,
          isDeleting,
          page,
          searchInput,
          columns,
          rowsPerPage,
          orderBy,
          sortBy,
          sortablesList,
        }}
      />
      <button
        className="text-[#F7F7F7]"
        onClick={() => {
          try {
            throw new Error('🔥 Test Sentry error from production');
          } catch (e) {
            Sentry.captureException(e);
          }
        }}
      >
        Break me
      </button>
    </>
  );
};

export default UsersTable;
